import { InboxAPI } from "api/inbox";
import { Attachment } from "models/dashboard/dashboard";
import {
  MessageItem,
  SelectedFilters,
  SentMessages,
  Thread,
  THREAD_CATEGORY_LABELS
} from "models/inbox/inbox";
import { NavigateFunction } from "react-router";
import { StateCreator } from "zustand";

import { isInternalUser } from "common/MetaActions";
import { getFiltersQueryParams } from "helpers/inbox/getFiltersQueryParams";
import {
  filterAndSortMessages,
  sortThreadsByLastMessage
} from "helpers/inbox/messageAndThreadHelpers";
import { RootState } from "store";

export const MessageType = {
  Normal: "normal",
  PeriodicReport: "report"
};

type ThreadsResponse = {
  items: Thread[];
  totalCount: number;
};

export interface InboxSlice {
  isLoadingThread: boolean;
  isLoadingMyThreads: boolean;
  isLoadingAllThreads: boolean;
  isLoadingSentMessages: boolean;
  myThreads: Thread[];
  allThreads: Thread[];
  myTotalThreads: number;
  totalThreads: number;
  sentMessages: MessageItem[];
  sentMessagesSearchTerm: string;
  selectedMessageId: string;
  isLoadingPnlData: boolean;
  pnlData: {
    content: string;
    isLoaded: boolean;
  };
  currentThread?: Thread;
  activeFilters: SelectedFilters & {
    categories: string[];
  };
  setActiveFilters: (
    activeFilters: SelectedFilters & {
      categories: string[];
    }
  ) => void;
  resetActiveFilters: () => void;
  loadingMyThreads: () => void;
  loadingAllThreads: () => void;
  loadingSentMessages: () => void;
  setMyThreads: (threads: Thread[], totalThreads: number) => void;
  setAllThreads: (threads: Thread[], totalThreads: number) => void;
  appendMyThreads: (threads: Thread[], total: number) => void;
  appendAllThreads: (threads: Thread[], total: number) => void;
  failedToLoadMyThreads: () => void;
  failedToLoadAllThreads: () => void;
  loadingThread: () => void;
  setThread: (thread: Thread) => void;
  failedToGetThread: () => void;
  setSentMessages: (messages: Omit<MessageItem, "subscribers">[]) => void;
  setSentMessagesSearchTerm: (searchTerm: string) => void;
  setSelectedMessageId: (messageId: string) => void;
  failedToLoadSentMessages: () => void;
  resetInbox: () => void;
  resetThreads: () => void;
  loadInbox: () => Promise<void>;
  fetchThread: (
    customerId: string,
    threadId: string,
    navigate?: NavigateFunction
  ) => Promise<void>;
  fetchMyThreads: (
    customerId: string,
    skip?: number,
    searchTerms?: string
  ) => Promise<void>;
  fetchAllThreads: (
    customerId: string,
    skip?: number,
    searchTerms?: string
  ) => Promise<void>;
  fetchMoreThreads: (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string
  ) => Promise<void>;
  setThreadStatus: (threadId: string, action: string) => Promise<void>;
  fetchSentMessages: (customerId: string) => Promise<void>;
  sendMessage: (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type?: string,
    reportStart?: string,
    reportEnd?: string,
    category?: number
  ) => Promise<void>;
  archiveThread: (customerId: string, threadId: string) => Promise<void>;
  unarchiveThread: (customerId: string, threadId: string) => Promise<void>;
  updateThreadCategory: (
    customerId: string,
    threadId: string,
    category: number
  ) => Promise<void>;
  hideThreadMessage: (
    customerId: string,
    messageId: string,
    threadId: string
  ) => Promise<void>;
  unhideThreadMessage: (
    customerId: string,
    messageId: string,
    threadId: string
  ) => Promise<void>;
}

export const createInboxSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  InboxSlice
> = (set, get) => ({
  isLoadingThread: false,
  isLoadingMyThreads: false,
  isLoadingAllThreads: false,
  isLoadingSentMessages: false,
  myThreads: [],
  allThreads: [],
  myTotalThreads: 0,
  totalThreads: 0,
  currentThread: undefined,
  sentMessages: [],
  sentMessagesSearchTerm: "",
  selectedMessageId: "",
  isLoadingPnlData: false,
  pnlData: {
    content: "",
    isLoaded: false
  },
  activeFilters: {
    flagged: false,
    unread: false,
    drafts: false,
    archived: false,
    categories: []
  },
  resetActiveFilters: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          activeFilters: {
            flagged: false,
            unread: false,
            drafts: false,
            archived: false,
            categories: []
          }
        }
      }),
      false,
      "inbox/RESET_ACTIVE_FILTERS"
    ),
  setActiveFilters: (
    activeFilters: SelectedFilters & {
      categories: string[];
    }
  ) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          activeFilters
        }
      }),
      false,
      "inbox/SET_ACTIVE_FILTERS"
    ),

  loadingMyThreads: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          isLoadingMyThreads: true
        }
      }),
      false,
      "inbox/LOADING_MY_THREADS"
    ),

  loadingAllThreads: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          isLoadingAllThreads: true
        }
      }),
      false,
      "inbox/LOADING_ALL_THREADS"
    ),

  loadingSentMessages: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          isLoadingSentMessages: true
        }
      }),
      false,
      "inbox/LOADING_SENT_MESSAGES"
    ),

  setMyThreads: (threads = [], total) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          myThreads: threads,
          myTotalThreads: total,
          isLoadingMyThreads: false
        }
      }),
      false,
      "inbox/SET_MY_THREADS"
    ),

  setAllThreads: (threads = [], total) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          allThreads: threads,
          totalThreads: total,
          isLoadingAllThreads: false
        }
      }),
      false,
      "inbox/SET_ALL_THREADS"
    ),

  appendMyThreads: (threads = [], total: number) =>
    set(
      (state) => {
        const newThreads = [...state.inbox.myThreads, ...threads];
        const sortedThreads = sortThreadsByLastMessage(newThreads);
        return {
          inbox: {
            ...state.inbox,
            myThreads: sortedThreads,
            myTotalThreads: total,
            isLoadingMyThreads: false
          }
        };
      },
      false,
      "inbox/APPEND_MY_THREADS"
    ),

  appendAllThreads: (threads = [], total: number) => {
    set(
      (state) => {
        const newThreads = [...state.inbox.allThreads, ...threads];
        const sortedThreads = sortThreadsByLastMessage(newThreads);
        return {
          inbox: {
            ...state.inbox,
            allThreads: sortedThreads,
            totalThreads: total,
            isLoadingAllThreads: false
          }
        };
      },
      false,
      "inbox/APPEND_ALL_THREADS"
    );
  },

  failedToLoadMyThreads: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          myThreads: [],
          myTotalThreads: 0,
          isLoadingMyThreads: false
        }
      }),
      false,
      "inbox/LOADING_MY_THREADS_FAILED"
    ),

  failedToLoadAllThreads: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          allThreads: [],
          totalThreads: 0,
          isLoadingAllThreads: false
        }
      }),
      false,
      "inbox/LOADING_ALL_THREADS_FAILED"
    ),

  loadingThread: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          isLoadingThread: true,
          currentThread: undefined
        }
      }),
      false,
      "inbox/LOADING_THREAD"
    ),

  setThread: (thread: Thread) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          currentThread: thread,
          allThreads: state.inbox.allThreads.map((t) => {
            if (t.threadId === thread.threadId) {
              return {
                ...thread,
                lastMessage: { ...thread.lastMessage, isRead: true },
                unread: false
              };
            }
            return t;
          }),
          myThreads: state.inbox.myThreads.map((t) => {
            if (t.threadId === thread.threadId) {
              return {
                ...thread,
                lastMessage: { ...thread.lastMessage, isRead: true },
                unread: false
              };
            }
            return t;
          }),
          isLoadingThread: false
        }
      }),
      false,
      "inbox/SET_THREAD"
    ),

  failedToGetThread: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          isLoadingThread: false
        }
      }),
      false,
      "inbox/FAILED_TO_GET_THREAD"
    ),

  setSentMessages: (messages: Omit<MessageItem, "subscribers">[]) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          sentMessages: messages,
          isLoadingSentMessages: false
        }
      }),
      false,
      "inbox/SET_SENT_MESSAGES"
    ),

  setSentMessagesSearchTerm: (searchTerm: string) =>
    set(
      ({ inbox }) => ({
        inbox: {
          ...inbox,
          sentMessagesSearchTerm: searchTerm
        }
      }),
      false,
      "inbox/SET_SENT_MESSAGES_SEARCH_TERM"
    ),

  setSelectedMessageId: (messageId: string) =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          selectedMessageId: messageId
        }
      }),
      false,
      "inbox/SET_SELECTED_MESSAGE_ID"
    ),

  failedToLoadSentMessages: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          sentMessages: [],
          isLoadingSentMessages: false
        }
      }),
      false,
      "inbox/LOADING_SENT_MESSAGES_FAILED"
    ),

  resetInbox: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          myThreads: [],
          allThreads: [],
          sentMessages: [],
          sentMessagesSearchTerm: "",
          currentThread: undefined
        }
      }),
      false,
      "inbox/INBOX_RESET"
    ),

  resetThreads: () =>
    set(
      (state) => ({
        inbox: {
          ...state.inbox,
          totalThreads: 0,
          myThreads: [],
          allThreads: [],
          currentThread: undefined
        }
      }),
      false,
      "inbox/THREADS_RESET"
    ),

  loadInbox: async () => {
    const workingCustomerId = get().global.workingCustomerId;
    get().inbox.resetInbox();

    try {
      await Promise.all([
        get().inbox.fetchMyThreads(workingCustomerId),
        get().inbox.fetchAllThreads(workingCustomerId),
        get().inbox.fetchSentMessages(workingCustomerId),
        get().archive.fetchArchiveFolders(workingCustomerId)
      ]);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av inkorgen misslyckades.",
        error
      );
    }
  },

  fetchThread: async (
    customerId: string,
    threadId: string,
    navigate?: NavigateFunction
  ) => {
    get().inbox.loadingThread();
    try {
      const thread = await InboxAPI.fetchThread(customerId, threadId);
      const { hasVisibleMessages } = filterAndSortMessages(thread.messages);

      // Navigate external users away from threads where all messages are hidden.
      if (!isInternalUser() && !hasVisibleMessages && navigate) {
        get().status.setUnreadThreadsStatus(customerId);
        navigate("/inbox");
        return;
      }

      get().status.setUnreadThreadsStatus(customerId);
      get().inbox.setThread(thread);
    } catch (error) {
      get().inbox.failedToGetThread();
      get().error.appendErrorNotification(
        `Hämtning av konversationen misslyckades ${threadId}`,
        error
      );
    }
  },

  fetchMyThreads: async (customerId: string, skip = 0, searchTerms = "") => {
    get().inbox.loadingMyThreads();

    const queryParams = getFiltersQueryParams(get().inbox.activeFilters);

    try {
      const result = await InboxAPI.fetchThreads(
        customerId,
        false,
        skip,
        searchTerms,
        queryParams
      );
      const model: ThreadsResponse = result;

      const sortedItems = sortThreadsByLastMessage(model.items);
      get().inbox.setMyThreads(sortedItems, model.totalCount);
    } catch (error) {
      get().inbox.failedToLoadMyThreads();
      get().error.appendErrorNotification(
        "Hämtning av konversationer misslyckades.",
        error
      );
    }
  },

  fetchAllThreads: async (customerId: string, skip = 0, searchTerms = "") => {
    if (!isInternalUser()) {
      return;
    }
    
    get().inbox.loadingAllThreads();
    const queryParams = getFiltersQueryParams(get().inbox.activeFilters);
    try {
      const result = await InboxAPI.fetchThreads(
        customerId,
        true,
        skip,
        searchTerms,
        queryParams
      );
      const model: ThreadsResponse = result;
      const sortedItems = sortThreadsByLastMessage(model.items);
      get().inbox.setAllThreads(sortedItems, model.totalCount);
    } catch (error) {
      get().inbox.failedToLoadAllThreads();
      get().error.appendErrorNotification(
        "Hämtning av konversationer misslyckades.",
        error
      );
    }
  },

  fetchMoreThreads: async (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string
  ) => {
    if (getAllThreads) {
      get().inbox.loadingAllThreads();
    } else {
      get().inbox.loadingMyThreads();
    }

    const queryParams = getFiltersQueryParams(get().inbox.activeFilters);

    try {
      const response = await InboxAPI.fetchMoreThreads(
        customerId,
        getAllThreads,
        skip,
        searchTerms,
        queryParams
      );

      const model: ThreadsResponse = response;

      if (getAllThreads) {
        get().inbox.appendAllThreads(model.items, model.totalCount);
      } else {
        get().inbox.appendMyThreads(model.items, model.totalCount);
      }
    } catch (error) {
      if (getAllThreads) {
        get().inbox.failedToLoadAllThreads();
      } else {
        get().inbox.failedToLoadMyThreads();
      }

      get().error.appendErrorNotification(
        "Ytterligare konversationer kunde inte laddas.",
        error
      );
    }
  },

  setThreadStatus: async (threadId: string, action: string) => {
    try {
      // Make API call to update status on backend
      await InboxAPI.setThreadStatus(threadId, action);

      const isUnread = action === "unread";

      set(
        (state) => ({
          inbox: {
            ...state.inbox,

            currentThread:
              state.inbox.currentThread?.threadId === threadId
                ? {
                    ...state.inbox.currentThread,
                    unread: isUnread,
                    lastMessage: {
                      ...state.inbox.currentThread.lastMessage,
                      isRead: !isUnread
                    }
                  }
                : state.inbox.currentThread,

            myThreads: state.inbox.myThreads.map((thread) =>
              thread.threadId === threadId
                ? {
                    ...thread,
                    unread: isUnread,
                    lastMessage: {
                      ...thread.lastMessage,
                      isRead: !isUnread
                    }
                  }
                : thread
            ),
            allThreads: state.inbox.allThreads.map((thread) =>
              thread.threadId === threadId
                ? {
                    ...thread,
                    unread: isUnread,
                    lastMessage: {
                      ...thread.lastMessage,
                      isRead: !isUnread
                    }
                  }
                : thread
            )
          }
        }),
        false,
        "inbox/SET_THREAD_STATUS"
      );

      // Update unread threads count in status
      const workingCustomerId = get().global.workingCustomerId;
      get().status.setUnreadThreadsStatus(workingCustomerId);
    } catch (error) {
      get().error.appendErrorNotification(
        "Det gick inte att uppdatera trådstatus",
        error
      );
    }
  },

  fetchSentMessages: async (customerId: string) => {
    try {
      const result = await InboxAPI.fetchSentMessages(customerId);
      const model: SentMessages = result;
      get().inbox.setSentMessages(model.messages);
    } catch (error) {
      get().inbox.failedToLoadSentMessages();
      get().error.appendErrorNotification(
        "Hämtning av meddelanden misslyckades.",
        error
      );
    }
  },

  sendMessage: async (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type = MessageType.Normal,
    reportStart = "",
    reportEnd = "",
    category?: number
  ) => {
    try {
      const result = await InboxAPI.sendMessage(
        fromUserId,
        body,
        title,
        threadId,
        subscribers,
        customerId,
        attachments,
        type,
        reportStart,
        reportEnd,
        category
      );

      const res = result;
      get().inbox.loadingThread();
      await get().inbox.loadInbox();
      get().inbox.fetchThread(customerId, res.threadId);
      return result;
    } catch (error) {
      get().error.appendErrorNotification(
        "Skickning av meddelandet misslyckades.",
        error
      );
      return Promise.reject();
    }
  },

  archiveThread: async (customerId: string, threadId: string) => {
    try {
      await InboxAPI.archiveThread(customerId, threadId);

      await get().inbox.loadInbox();
      get().toastNotifications.addNotification(
        "info",
        "Konversationen har arkiverats"
      );
    } catch (e) {
      get().error.appendErrorNotification(
        "Fel vid arkivering av konversation",
        e
      );
    }
  },

  unarchiveThread: async (customerId: string, threadId: string) => {
    try {
      await InboxAPI.unarchiveThread(customerId, threadId);

      await get().inbox.loadInbox();
      get().toastNotifications.addNotification(
        "info",
        "Konversationen har återställts från arkivet"
      );
    } catch (e) {
      get().error.appendErrorNotification(
        "Fel vid återläsning av konversation från arkivet",
        e
      );
    }
  },

  updateThreadCategory: async (
    customerId: string,
    threadId: string,
    category: number
  ) => {
    try {
      const decodedThreadId = decodeURIComponent(threadId || "");
      await InboxAPI.updateThreadCategory(customerId, decodedThreadId, category);

      // Update the thread's category in the store
      set((state) => ({
        inbox: {
          ...state.inbox,
          currentThread: state.inbox.currentThread?.threadId === decodedThreadId
            ? {
                ...state.inbox.currentThread,
                category
              }
            : state.inbox.currentThread,
          allThreads: state.inbox.allThreads.map((thread) =>
            thread.threadId === decodedThreadId
              ? {
                  ...thread,
                  category
                }
              : thread
          ),
          myThreads: state.inbox.myThreads.map((thread) =>
            thread.threadId === decodedThreadId
              ? {
                  ...thread,
                  category
                }
              : thread
          )
        }
      }));

      get().toastNotifications.addNotification(
        "info",
        `Kategori ändrad till: ${THREAD_CATEGORY_LABELS[category] || "Okänd"}`
      );
    } catch (e) {
      get().error.appendErrorNotification(
        "Fel vid uppdatering av kategori",
        e
      );
    }
  },

  hideThreadMessage: async (
    customerId: string,
    messageId: string,
    threadId: string
  ) => {
    try {
      await InboxAPI.hideThreadMessage(customerId, messageId);
      await get().inbox.fetchThread(customerId, threadId);
      get().inbox.loadInbox();

      get().toastNotifications.addNotification("info", "Meddelandet har dolts");
    } catch (e) {
      get().error.appendErrorNotification(
        "Det gick inte att dölja meddelandet",
        e
      );
    }
  },

  unhideThreadMessage: async (
    customerId: string,
    messageId: string,
    threadId: string
  ) => {
    try {
      await InboxAPI.unhideThreadMessage(customerId, messageId);

      await get().inbox.fetchThread(customerId, threadId);
      get().inbox.loadInbox();
      get().toastNotifications.addNotification(
        "info",
        "Meddelandet har återställts"
      );
    } catch (e) {
      get().error.appendErrorNotification(
        "Det gick inte att återställa meddelandet",
        e
      );
    }
  }
});
